/**
 * MediaPicker — "Wybierz z biblioteki"
 * Reusable modal/inline component to pick photos from media_library.
 * Props:
 *   onSelect(item)   — called when user picks a photo
 *   onClose()        — close the picker
 *   tags             — optional filter by usage_tags (e.g. "profile,hero")
 *   multiple         — allow selecting multiple (default: false)
 *   open             — controlled visibility
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Upload, Loader2, Camera, ImageIcon } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';

export default function MediaPicker({ onSelect, onClose, tags, multiple = false, open = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const url = tags
          ? `${API}/media-library?tags=${encodeURIComponent(tags)}`
          : `${API}/media-library`;
        const res = await axios.get(url, { headers });
        setItems(res.data.items || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchMedia();
  }, [open, tags]); // eslint-disable-line

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API}/media-library/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        setItems(prev => [res.data, ...prev]);
      } catch { /* skip */ }
    }
    setUploading(false);
    e.target.value = '';
  };

  const toggleSelect = (item) => {
    if (multiple) {
      setSelected(prev =>
        prev.some(s => s.id === item.id)
          ? prev.filter(s => s.id !== item.id)
          : [...prev, item]
      );
    } else {
      onSelect(item);
    }
  };

  const confirmMultiple = () => {
    if (selected.length > 0) onSelect(selected);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid="media-picker-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl w-[90vw] max-w-[640px] max-h-[80vh] flex flex-col overflow-hidden"
          data-testid="media-picker"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#F47A3B]" />
              <h2 className="text-[16px] font-bold text-zinc-900">Wybierz z biblioteki</h2>
            </div>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600" data-testid="media-picker-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Upload bar */}
          <div className="px-5 py-3 border-b border-zinc-50 bg-zinc-25 flex items-center justify-between">
            <p className="text-[13px] text-zinc-500">
              {items.length} {items.length === 1 ? 'zdjęcie' : 'zdjęć'} w bibliotece
            </p>
            <div className="relative">
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
              <Button size="sm" variant="outline" className="rounded-lg text-[12px] h-8" disabled={uploading} data-testid="media-picker-upload">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                Dodaj nowe
              </Button>
            </div>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 text-[#F47A3B] animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <Camera className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                <p className="text-[14px] text-zinc-500 mb-1">Brak zdjęć w bibliotece</p>
                <p className="text-[12px] text-zinc-400">Dodaj zdjęcia powyżej lub w Profilu</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="media-picker-grid">
                {items.map(item => {
                  const isSelected = selected.some(s => s.id === item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleSelect(item)}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all group ${
                        isSelected ? 'border-[#F47A3B] ring-2 ring-[#F47A3B]/20' : 'border-zinc-200 hover:border-[#F47A3B]/40'
                      }`}
                      data-testid={`media-pick-${item.id}`}
                    >
                      <img
                        src={getImageUrl(item.thumbnail_url || item.file_url)}
                        alt={item.alt_text || item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-[#F47A3B]/20 flex items-center justify-center">
                          <div className="w-7 h-7 bg-[#F47A3B] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer (multiple mode) */}
          {multiple && selected.length > 0 && (
            <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between">
              <p className="text-[13px] text-zinc-500">Wybrano: {selected.length}</p>
              <Button onClick={confirmMultiple} className="bg-[#F47A3B] hover:bg-[#E06A2B] rounded-xl text-[13px]" data-testid="media-picker-confirm">
                Potwierdź wybór
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
